import React, { useState, useEffect } from 'react';
import sendgridService from '../../services/sendgrid';
import { useAuth } from "context/Auth";
import { ClipboardDocumentIcon, EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ApiKeysSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [newApiKey, setNewApiKey] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [apiKeyName, setApiKeyName] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [isDialogSubmitting, setIsDialogSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const { user } = useAuth();

  const fetchApiKeys = async () => {
    if (!user?.currentOrganization?.sendgridConfig?.user_id) return;

    setLoading(true);
    setError(null);

    try {
      const apiKeys = await sendgridService.getApiKeys();
      setApiKeys(apiKeys);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!apiKeyName.trim() || !user?.currentOrganization?.sendgridConfig?.user_id) return;

    setIsDialogSubmitting(true);
    setDialogError(null);

    try {
      const result = await sendgridService.createApiKey(apiKeyName);
      console.log(result);
      setNewApiKey(result);
    } catch (err: any) {
      setDialogError(err.response?.data?.message || 'Failed to create API key');
    } finally {
      setIsDialogSubmitting(false);
    }
  };

  const handleDeleteApiKey = async (apiKeyId: string) => {
    try {
      await sendgridService.deleteApiKey(apiKeyId);
      await fetchApiKeys();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete API key');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setSnackbar({ open: true, message: 'API key copied to clipboard!' });
        setTimeout(() => setSnackbar({ open: false, message: '' }), 3000);
      },
      () => {
        setSnackbar({ open: true, message: 'Failed to copy API key' });
        setTimeout(() => setSnackbar({ open: false, message: '' }), 3000);
      }
    );
  };

  const toggleShowSecret = () => {
    setShowSecret(!showSecret);
  };

  const handleCloseDialog = async () => {
    setOpenDialog(false);
    setApiKeyName('');
    setNewApiKey(null);
    setDialogError(null);
    await fetchApiKeys();
  };

  useEffect(() => {
    if (user?.currentOrganization?.sendgridConfig?.user_id) {
      fetchApiKeys();
    }
  }, [user?.currentOrganization?.sendgridConfig?.user_id]);

  if (!user?.currentOrganization?.sendgridConfig?.user_id) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              You need to enable email in the General Settings tab before configuring API keys.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
          {error}
        </div>
      )}

      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">API Keys</h2>
          <button
            onClick={() => setOpenDialog(true)}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Create New API Key
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          API keys are used to authenticate with the SendGrid API. Create API keys to access your SendGrid account programmatically.
        </p>

        {loading ? (
          <div className="flex justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  No API keys configured yet. Create an API key to access SendGrid programmatically.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {apiKeys.map((apiKey) => {
              console.log(apiKey);
              return (
                <div key={apiKey.api_key_id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">{apiKey.name}</h3>
                      <button
                        onClick={() => handleDeleteApiKey(apiKey.api_key_id)}
                        className="px-3 py-1 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      >
                        Revoke
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">API Key ID</p>
                        <p className="text-sm font-mono bg-gray-50 p-2 rounded">{apiKey.api_key_id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Key</p>
                        <p className="text-sm font-mono bg-gray-50 p-2 rounded">
                          {apiKey.api_key.slice(0, 4)}**********{apiKey.api_key.slice(-4)}
                          <button
                            onClick={() => copyToClipboard(apiKey.api_key)}
                            className="ml-2 rounded-md text-gray-400 hover:text-gray-600 focus:outline-none"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </p>

                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Modal for creating API key */}
      {openDialog && (
        <div className="fixed inset-0 z-10 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleCloseDialog}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              {newApiKey ? (
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      API Key Created Successfully
                    </h3>
                    <button
                      className="text-gray-400 hover:text-gray-500"
                      onClick={handleCloseDialog}
                    >
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          This API key will only be shown once. Please save it somewhere secure.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Key
                    </label>
                    <div className="relative">
                      <input
                        type={'text'}
                        value={showSecret ? newApiKey.api_key : newApiKey.api_key_preview}
                        readOnly
                        className="block w-full pr-20 border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-1">
                        <button
                          onClick={toggleShowSecret}
                          className="p-1 rounded-md text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                          {showSecret ? <EyeSlashIcon className="h-6 w-6" aria-hidden="true" /> : <EyeIcon className="h-6 w-6" aria-hidden="true" />}
                        </button>
                        <button
                          onClick={() => copyToClipboard(newApiKey.api_key)}
                          className="p-1 rounded-md text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                          <ClipboardDocumentIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Key ID
                    </label>
                    <input
                      type="text"
                      value={newApiKey.api_key_id}
                      readOnly
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                    />
                  </div>

                  <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end">
                    <button
                      type="button"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      onClick={handleCloseDialog}
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Create API Key
                    </h3>

                    {dialogError && (
                      <div className="mt-3 bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm">
                        {dialogError}
                      </div>
                    )}

                    <div className="mt-4">
                      <label htmlFor="apiKeyName" className="block text-sm font-medium text-gray-700">
                        API Key Name
                      </label>
                      <input
                        type="text"
                        name="apiKeyName"
                        id="apiKeyName"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="My API Key"
                        value={apiKeyName}
                        onChange={(e) => setApiKeyName(e.target.value)}
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Give your API key a descriptive name to remember what it's used for.
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${isDialogSubmitting || !apiKeyName.trim()
                        ? 'bg-blue-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                        }`}
                      onClick={handleCreateApiKey}
                      disabled={isDialogSubmitting || !apiKeyName.trim()}
                    >
                      {isDialogSubmitting ? (
                        <div className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating...
                        </div>
                      ) : 'Create API Key'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={handleCloseDialog}
                      disabled={isDialogSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Snackbar notification */}
      {snackbar.open && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg">
          {snackbar.message}
        </div>
      )}
    </div>
  );
};

export default ApiKeysSettings; 